import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import { uploadToCloudinary } from '../utils/cloudinary';

const PackageForm = ({ package: editPackage, onSave, onClose, setMessage }) => {
    const { addPackage, updatePackage } = useAdmin();
    const [loading, setLoading] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [imageSource, setImageSource] = useState('url');

    const [formData, setFormData] = useState({
        name: '',
        price: '',
        duration: '',
        location: '',
        persons: '',
        description: '',
        image: '',
        rating: 5
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (editPackage) {
            setFormData({
                name: editPackage.name || '',
                price: editPackage.price || '',
                duration: editPackage.duration || '',
                location: editPackage.location || '',
                persons: editPackage.persons || '',
                description: editPackage.description || '',
                image: editPackage.image || '',
                rating: editPackage.rating || 5
            });

            if (editPackage.image && editPackage.image.includes('cloudinary.com')) {
                setImageSource('upload');
            } else {
                setImageSource('url');
            }
        }
    }, [editPackage]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setMessage('Please select a valid image file (JPEG, PNG, WebP)');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setMessage('Image size should be less than 5MB');
            return;
        }

        try {
            setImageUploading(true);
            setMessage('Uploading image to Cloudinary...');

            const result = await uploadToCloudinary(file);

            setFormData(prev => ({
                ...prev,
                image: result.url
            }));

            setImageSource('upload');
            setMessage('Image uploaded successfully!');
        } catch (error) {
            console.error('Cloudinary upload error:', error);
            setMessage('Error uploading image: ' + error.message);
        } finally {
            setImageUploading(false);
        }
    };

    const handleImageUrlChange = (e) => {
        setFormData(prev => ({
            ...prev,
            image: e.target.value
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = 'Package name is required';
        if (!formData.price || formData.price <= 0) newErrors.price = 'Valid price is required';
        if (!formData.duration.trim()) newErrors.duration = 'Duration is required';
        if (!formData.location.trim()) newErrors.location = 'Location is required';
        if (!formData.persons || formData.persons < 1) newErrors.persons = 'Number of persons is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';
        if (!editPackage && !formData.image) newErrors.image = 'Image is required for new packages';

        if (formData.description.length > 1000) {
            newErrors.description = 'Description should not exceed 1000 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            setMessage('Please fix the errors in the form');
            return;
        }

        try {
            setLoading(true);
            setMessage(editPackage ? 'Updating package...' : 'Creating package...');

            const packageData = {
                name: formData.name.trim(),
                price: parseFloat(formData.price),
                duration: formData.duration.trim(),
                location: formData.location.trim(),
                persons: parseInt(formData.persons),
                description: formData.description.trim(),
                image: formData.image,
                rating: parseInt(formData.rating) || 5
            };

            if (editPackage) {
                await updatePackage(editPackage._id, packageData);
                setMessage('Package updated successfully!');
            } else {
                await addPackage(packageData);
                setMessage('Package added successfully!');
            }

            onSave();
        } catch (error) {
            console.error('Package save error:', error);
            setMessage('Error: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const removeImage = () => {
        setFormData(prev => ({
            ...prev,
            image: ''
        }));
        setImageSource('url');
    };

    return (
        <div
            className="modal fade show d-block"
            style={{
                backgroundColor: 'rgba(0,0,0,0.5)',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1050
            }}
        >
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 shadow-lg">
                    <div className="modal-header bg-primary text-white sticky-top">
                        <h5 className="modal-title fw-bold">
                            <i className={`fas ${editPackage ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                            {editPackage ? 'Edit Package' : 'Add New Package'}
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            disabled={loading || imageUploading}
                            aria-label="Close"
                        ></button>
                    </div>

                    <div className="modal-body" style={{ overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">
                                        Package Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                        placeholder="Enter package name"
                                        disabled={loading}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">
                                        Price ($) <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleChange}
                                        step="1"
                                        min="0"
                                        className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                                        placeholder="Enter price"
                                        disabled={loading}
                                    />
                                    {errors.price && <div className="invalid-feedback">{errors.price}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">
                                        Duration <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="duration"
                                        value={formData.duration}
                                        onChange={handleChange}
                                        className={`form-control ${errors.duration ? 'is-invalid' : ''}`}
                                        placeholder="e.g., 3 days 2 nights"
                                        disabled={loading}
                                    />
                                    {errors.duration && <div className="invalid-feedback">{errors.duration}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">
                                        Location <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                                        placeholder="Enter destination location"
                                        disabled={loading}
                                    />
                                    {errors.location && <div className="invalid-feedback">{errors.location}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">
                                        Number of Persons <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="persons"
                                        value={formData.persons}
                                        onChange={handleChange}
                                        min="1"
                                        max="50"
                                        className={`form-control ${errors.persons ? 'is-invalid' : ''}`}
                                        placeholder="Enter number of persons"
                                        disabled={loading}
                                    />
                                    {errors.persons && <div className="invalid-feedback">{errors.persons}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">Rating</label>
                                    <select
                                        name="rating"
                                        value={formData.rating}
                                        onChange={handleChange}
                                        className="form-select"
                                        disabled={loading}
                                    >
                                        <option value="5">⭐️⭐️⭐️⭐️⭐️ 5 Stars</option>
                                        <option value="4">⭐️⭐️⭐️⭐️ 4 Stars</option>
                                        <option value="3">⭐️⭐️⭐️ 3 Stars</option>
                                        <option value="2">⭐️⭐️ 2 Stars</option>
                                        <option value="1">⭐️ 1 Star</option>
                                    </select>
                                </div>

                                <div className="col-12">
                                    <label className="form-label fw-semibold">
                                        Description <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="4"
                                        className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                        placeholder="Enter detailed package description"
                                        disabled={loading}
                                        maxLength="1000"
                                    />
                                    {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                                    <div className="form-text text-end">
                                        {formData.description.length}/1000 characters
                                    </div>
                                </div>

                                <div className="col-12">
                                    <label className="form-label fw-semibold">
                                        Package Image
                                        {!editPackage && <span className="text-danger"> *</span>}
                                    </label>

                                    <div className="mb-3">
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="packageImageSource"
                                                id="packageImageSourceUrl"
                                                value="url"
                                                checked={imageSource === 'url'}
                                                onChange={() => setImageSource('url')}
                                                disabled={loading || imageUploading}
                                            />
                                            <label className="form-check-label" htmlFor="packageImageSourceUrl">
                                                Use Image URL
                                            </label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="packageImageSource"
                                                id="packageImageSourceUpload"
                                                value="upload"
                                                checked={imageSource === 'upload'}
                                                onChange={() => setImageSource('upload')}
                                                disabled={loading || imageUploading}
                                            />
                                            <label className="form-check-label" htmlFor="packageImageSourceUpload">
                                                Upload Image
                                            </label>
                                        </div>
                                    </div>

                                    {imageSource === 'url' && (
                                        <div className="mb-3">
                                            <input
                                                type="text"
                                                className={`form-control ${errors.image ? 'is-invalid' : ''}`}
                                                value={formData.image}
                                                onChange={handleImageUrlChange}
                                                placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                                                disabled={loading || imageUploading}
                                            />
                                            <div className="form-text">
                                                Enter full image URL. Make sure the URL is accessible.
                                            </div>
                                        </div>
                                    )}

                                    {imageSource === 'upload' && (
                                        <div className="mb-3">
                                            <div className="input-group">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageUpload}
                                                    className={`form-control ${errors.image ? 'is-invalid' : ''}`}
                                                    disabled={loading || imageUploading}
                                                />
                                            </div>
                                            <div className="form-text">
                                                Supported formats: JPG, PNG, WebP. Max size: 5MB
                                            </div>
                                        </div>
                                    )}

                                    {errors.image && <div className="invalid-feedback d-block">{errors.image}</div>}

                                    {imageUploading && (
                                        <div className="alert alert-info mt-2 mb-0">
                                            <div className="d-flex align-items-center">
                                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span>Uploading image to Cloudinary...</span>
                                            </div>
                                        </div>
                                    )}

                                    {formData.image && (
                                        <div className="mt-3 p-3 border rounded bg-light">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <strong className="text-success">
                                                    <i className="fas fa-check-circle me-1"></i>
                                                    {imageSource === 'upload' ? 'Image Uploaded to Cloudinary' : 'Image URL Set'}
                                                </strong>
                                                <button
                                                    type="button"
                                                    onClick={removeImage}
                                                    className="btn btn-outline-danger btn-sm"
                                                    disabled={loading || imageUploading}
                                                >
                                                    <i className="fas fa-times me-1"></i>
                                                    Remove Image
                                                </button>
                                            </div>
                                            <div className="text-center">
                                                <img
                                                    src={formData.image}
                                                    alt="Package Preview"
                                                    className="img-fluid rounded shadow-sm"
                                                    style={{ maxHeight: '200px', objectFit: 'cover' }}
                                                />
                                                <div className="mt-2">
                                                    <small className="text-muted">
                                                        {imageSource === 'upload' ? 'Hosted on Cloudinary CDN' : 'From provided URL'}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer sticky-bottom bg-light pt-3 mt-4 border-top">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onClose}
                                    disabled={loading || imageUploading}
                                >
                                    <i className="fas fa-times me-1"></i>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading || imageUploading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            {editPackage ? 'Updating...' : 'Creating...'}
                                        </>
                                    ) : (
                                        <>
                                            <i className={`fas ${editPackage ? 'fa-save' : 'fa-plus'} me-1`}></i>
                                            {editPackage ? 'Update Package' : 'Create Package'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PackageForm;